import React from 'react';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ConsentState, loadConsent, saveConsent } from './consent';

type Props = {
  onConsentResolved?: (state: ConsentState) => void;
};

export function ConsentBanner({ onConsentResolved }: Props) {
  const [state, setState] = React.useState<ConsentState | null>(null);
  const [monitoring, setMonitoring] = React.useState(true);
  const theme = useTheme();

  const textColor = 'rgba(0, 0, 0, 0.6)';

  React.useEffect(() => {
    loadConsent().then((s) => {
      setState(s);
      setMonitoring(s.monitoring ?? true);
      onConsentResolved?.(s);
    });
  }, [onConsentResolved]);

  if (!state) return null;

  // Afficher la popin uniquement si l'utilisateur n'a pas encore choisi pour le monitoring
  if (state.monitoring !== null) return null;

  const persist = async (options: Omit<ConsentState, 'necessary' | 'updatedAt'>) => {
    const consent: ConsentState = {
      ...options,
      necessary: true,
      updatedAt: new Date().toISOString(),
    };

    await saveConsent(consent);
    setState(consent);
    onConsentResolved?.(consent);
  };

  const accept = async () => {
    await persist({
      monitoring: true,
    });
  };

  const refuse = async () => {
    await persist({
      monitoring: false,
    });
  };

  const save = async () => {
    await persist({
      monitoring: monitoring,
    });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 0,
        bottom: 0,
        zIndex: 2000,
        p: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          '& *': {
            textShadow: 'none',
            filter: 'none',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'black' }}>
            Confidentialité 🍪
          </Typography>
          <Typography
            variant="caption"
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
              color: textColor,
              '&:hover': {
                color: 'black',
              },
            }}
            onClick={refuse}
          >
            Continuer sans accepter
          </Typography>
        </Stack>

        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: textColor }}>
          Choisi tes préférences en matière de confidentialité.
        </Typography>

        <Stack
          divider={<Divider sx={{ borderColor: textColor }} />}
          spacing={1}
          my={2}
          p={2}
          sx={{
            border: '1px solid #c9c9c9',
            borderRadius: 2,
            backgroundColor: 'white',
          }}
        >
          <FormControlLabel
            control={<Switch color="success" checked disabled />}
            label={
              <Box>
                <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 700, color: textColor }}>
                  Authentification (nécessaires)
                </Typography>
                <Typography variant="caption" sx={{ color: textColor }}>
                  Indispensable pour vous connecter et sécuriser votre session.
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                color="success"
                checked={monitoring}
                onChange={(e) => setMonitoring(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: textColor }}>
                  Monitoring
                </Typography>
                <Typography variant="caption" sx={{ color: textColor }}>
                  Permet d’envoyer des rapports d’erreurs pour améliorer l’application.
                </Typography>
              </Box>
            }
          />
        </Stack>

        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Button size="small" color="success" variant="contained" onClick={accept}>
            Tout accepter
          </Button>

          <Button size="small" color="uncolored" variant="outlined" onClick={save}>
            Enregistrer
          </Button>

          <Button
            color="uncolored"
            size="small"
            variant="text"
            startIcon={<theme.icons.info />}
            onClick={() => window.open('/privacy-policy', '_blank')}
            sx={{ marginLeft: 'auto' }}
          >
            En savoir plus
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
